import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import InlineHiringInsights from './InlineHiringInsights';
import reportWebVitals from './reportWebVitals';
import {
  replaceMonthPlaceholder,
  i18nDateFormat,
} from './utils/localizationUtils';
import { isNonProdPreviewHost, applyPreviewSampleData } from './previewSampleData';

// Get all instances of the modal shortcode output.
const modals = document.querySelectorAll('[data-job-title-modal]');

function getHIData(modalId: string, paramKey: string) {
  const tag = document.querySelector(
    `[data-param="${paramKey}"][data-modal-id="${modalId}"]`
  );
  if (!tag || !tag.textContent) {
    return '';
  }
  return tag.textContent;
}

function getHIDataIndex(
  modalId: string,
  paramKey: string,
  index: number
): string {
  const tag = document.querySelector(
    `[data-param="${paramKey}"][data-modal-id="${modalId}"][data-index="${index}"]`
  );
  if (!tag || !tag.textContent) {
    return '';
  }
  return tag.textContent;
}

function initializeJobSeekerSection(locale: string, sourceDate: string, previousFromDate: string | undefined, modalId: string, modalProps:any) {
  const jobSeekerClick = getHIData(modalId, 'ctk_count');
  const previousJobSeekerClick = getHIData(modalId, 'prior_ctk_count');
  const jobSeekerClickDiff =
      parseInt(jobSeekerClick) - parseInt(previousJobSeekerClick);
  const currentSectionName = 'job_seekers';
  const currentSection = modalProps.modalData.sections.find(
      (section: { name: string }) => section.name === currentSectionName
  );
  if (currentSection) {
    // label, tooltip_info and description are only set in Job title modal ACF in WP
    // the other fields will fall back to the ACF values if they don't exist in RAD(This probably won't happen in production, only for easy local debugging)
    const currentSectionData = {
      name: currentSectionName,
      label: currentSection.label,
      change_amount: String(jobSeekerClickDiff || currentSection.change_amount), // The diff result might be 0. stringify it so the check below won't file
      amount: jobSeekerClick || currentSection.amount,
      date: previousFromDate,
      tooltip_info: replaceMonthPlaceholder(
          currentSection.tooltip_info,
          i18nDateFormat(sourceDate, locale)
      ),
      description: currentSection.description,
    };
    // only display the section if every field in currentSectionData is set
    if (Object.values(currentSectionData).every((field) => field)) {
      return currentSectionData;
    }
  }
}

function initializeSalarySection(locale: string, sourceDate: string, modalId: string, modalProps:any) {
  const minSalary = getHIData(modalId, 'min_salary');
  const averageSalary = getHIData(modalId, 'average_salary');
  const maxSalary = getHIData(modalId, 'max_salary');
  const payPeriod = getHIData(modalId, 'pay_period');
  const currentSectionName = 'average_salary';
  const currentSection = modalProps.modalData.sections.find(
      (section: { name: string }) => section.name === currentSectionName
  );
  if (currentSection) {
    // label, tooltip_info and description are set in Job title modal ACF in WP
    const currentSectionData = {
      name: currentSectionName,
      label: currentSection.label,
      tooltip_info: replaceMonthPlaceholder(
          currentSection.tooltip_info,
          i18nDateFormat(sourceDate, locale)
      ),
      high: maxSalary || currentSection.high,
      low: minSalary || currentSection.low,
      average: averageSalary || currentSection.average,
      unit: payPeriod || currentSection.unit,
    };
    // only display the section if every field in currentSectionData is set
    if (Object.values(currentSectionData).every((field) => field)) {
      return currentSectionData;
    }
  }
}

function initializeEmployerSection(locale: string, sourceDate: string, modalId: string, modalProps:any) {
  const employersCount = getHIData(modalId, 'num_employers_actively_hiring');
  const currentSectionName = 'employers';
  const currentSection = modalProps.modalData.sections.find(
      (section: { name: string }) => section.name === currentSectionName
  );
  if (currentSection) {
    // label, tooltip_info and description are set in Job title modal ACF in WP
    const currentSectionData = {
      name: currentSectionName,
      label: currentSection.label,
      tooltip_info: replaceMonthPlaceholder(
          currentSection.tooltip_info,
          i18nDateFormat(sourceDate, locale)
      ),
      description: currentSection.description,
      amount: employersCount || currentSection.amount,
    };
    // only display the section if every field in currentSectionData is set
    if (Object.values(currentSectionData).every((field) => field)) {
      return currentSectionData;
    }
  }
}

function initializeResumeSection(locale: string, sourceDate: string, previousFromDate: string | undefined, modalId: string, modalProps:any) {
  const totalResumes = getHIData(modalId, 'total_resumes');
  const previousTotalResumes = getHIData(modalId, 'prior_total_resumes');
  const resumeDiff = parseInt(totalResumes) - parseInt(previousTotalResumes);
  const currentSectionName = 'resumes';
  const currentSection = modalProps.modalData.sections.find(
      (section: { name: string }) => section.name === currentSectionName
  );
  if (currentSection) {
    // label, tooltip_info and description are set in Job title modal ACF in WP
    const currentSectionData = {
      name: currentSectionName,
      label: currentSection.label,
      tooltip_info: replaceMonthPlaceholder(
          currentSection.tooltip_info,
          i18nDateFormat(sourceDate, locale)
      ),
      description: currentSection.description,
      amount: totalResumes || currentSection.amount,
      change_amount: String(resumeDiff || currentSection.change_amount), // The diff result might be 0. stringify it so the check below won't file
      date: previousFromDate,
    };
    // only display the section if every field in currentSectionData is set
    if (Object.values(currentSectionData).every((field) => field)) {
      return currentSectionData;
    }
  }
}


function initializeTopTerms(locale: string, sourceDate: string, modalId: string, modalProps:any) {
  const currentSectionName = 'top_5_search_terms';
  const currentSection = modalProps.modalData.sections.find(
      (section: { name: string }) => section.name === currentSectionName
  );
  if (currentSection) {
    const topTerms = [];
    const topPercents = [];
    for (let i = 0; i < 5; i++) {
      const term = getHIDataIndex(modalId, 'search_term_list', i);
      const percent = getHIDataIndex(modalId, 'click_percent_list', i);
      if (term && percent) {
        topTerms.push(term);
        topPercents.push(percent);
      }
    }
    // label and tooltip_info are set in Job title modal ACF in WP
    const currentSectionData = {
      name: currentSectionName,
      label: currentSection.label,
      tooltip_info: replaceMonthPlaceholder(
          currentSection.tooltip_info,
          i18nDateFormat(sourceDate, locale)
      ),
      terms: topTerms && topTerms.length && topTerms.length === topPercents.length ? topTerms : currentSection.terms,
      percentStrings: topPercents && topPercents.length && topTerms.length === topPercents.length ? topPercents : currentSection.percentStrings,
    };
    // only display the section if every field in currentSectionData is set and top terms array is not empty
    if (
        currentSectionData.terms &&
        Object.values(currentSectionData).every((field) => field)
    ) {
      currentSectionData.terms = currentSectionData.terms.filter(
          (term: any) => term
      );
      if (currentSectionData.terms.length) {
        return currentSectionData;
      }
    }
  }
}


// modalProps contains the ACF fields initially, this function will merge it with dynamically injected HI RAD data from HTML tags
function mergeModalData(modalId: string, modalProps: any) {
  if (!modalId || !modalProps || !modalProps.modalData) {
    return;
  }
  const locale = modalProps.modalData.localization.locale; // this is guaranteed to exist since it's derived from WP's built-in function
  let sourceDate, previousFromDate;
  if (
    getHIData(modalId, 'from_date') &&
    getHIData(modalId, 'prior_from_date')
  ) {
    // Use dates from RAD first
    sourceDate = getHIData(modalId, 'from_date');
    previousFromDate = getHIData(modalId, 'prior_from_date');
    modalProps.modalData.sourceDate = sourceDate;
  } else if (modalProps.modalData.sourceDate) {
    // when developing locally RAD date doesn't exit, use page ACF date
    sourceDate = modalProps.modalData.sourceDate;
    // setDate(0) will get last day of previous month
    try {
      previousFromDate = new Date(new Date(sourceDate).setDate(0)).toISOString();
    } catch (e) {
      previousFromDate = sourceDate;
    }
  } else {
    // otherwise make sure they are undefined so that we will display "Invalid date" string instead of wrong data to the user
    sourceDate = undefined;
    previousFromDate = undefined;
  }

  const jobSeekerSection = initializeJobSeekerSection(locale, sourceDate, previousFromDate, modalId, modalProps);
  const salarySection = initializeSalarySection(locale, sourceDate, modalId, modalProps);
  const employerSection = initializeEmployerSection(locale, sourceDate, modalId, modalProps);
  const resumeSection = initializeResumeSection(locale, sourceDate, previousFromDate, modalId, modalProps);
  const topTermsSection = initializeTopTerms(locale, sourceDate, modalId, modalProps);

  // Must be initialized in this order to match the design
  modalProps.modalData.sections = [jobSeekerSection, salarySection, employerSection, resumeSection, topTermsSection].filter(section => section);;
}


function mergeIfHIDataPresent(id: string, props: any): boolean {
  const hasHIData = Array.from(
    document.querySelectorAll(`[data-param][data-modal-id="${id}"]`)
  ).some(tag => !!tag.textContent?.trim());
  if (hasHIData) {
    mergeModalData(id, props);
  }
  return hasHIData;
}

// Render a separate component for each instance.
modals.forEach((modal) => {
  const modalId = modal.getAttribute('data-job-title-modal') as string;

  let modalProps = (window as any)[modalId];
  const hasHIData = modalProps?.modalData
    ? mergeIfHIDataPresent(modalId, modalProps)
    : false;
  // On preview hosts only (never prod/qa), render clearly-labeled sample data — both for local
  // dev (no WordPress) and WP preview pages (ACF labels, no numbers) — instead of nothing/NaN.
  if (!hasHIData && isNonProdPreviewHost()) {
    modalProps = applyPreviewSampleData(modalId);
  }

  if (!modalProps?.modalData) return;

  const root = ReactDOM.createRoot(document.getElementById(modalId)!);
  root.render(
    <React.StrictMode>
      <App
        title={modalProps.title}
        displayTitle={modalProps.display}
        modalData={modalProps.modalData}
      />
    </React.StrictMode>
  );
});

// Mount inline hiring insights (expanded linking widget) — renders Job Seekers, Resumes, Average Salary without a modal.
const inlineContainers = document.querySelectorAll('[data-job-title-modal-inline]');
inlineContainers.forEach((container) => {
  const inlineId = container.getAttribute('data-job-title-modal-inline') as string;

  let inlineProps = (window as any)[inlineId];
  let hasData = inlineProps?.modalData
    ? mergeIfHIDataPresent(inlineId, inlineProps)
    : false;
  // On preview hosts only (never prod/qa), fall back to labeled sample data instead of NaN.
  if (!hasData && isNonProdPreviewHost()) {
    inlineProps = applyPreviewSampleData(inlineId);
    hasData = !!inlineProps?.modalData;
  }
  if (!hasData || !inlineProps?.modalData) return;

  const el = document.getElementById(inlineId);
  if (!el) return;
  const inlineRoot = ReactDOM.createRoot(el);
  inlineRoot.render(
    <InlineHiringInsights
      displayTitle={inlineProps.display}
      modalData={inlineProps.modalData}
    />
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
